import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import { theme } from '../../../theme';
import { axiosClient } from '../../../services/axiosClient';

const NoShowsList = () => {
  const [noShows, setNoShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, noShowId: null });
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchNoShows();
  }, [page, pageSize]);

  const fetchNoShows = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get('/superadmin/no-shows/', {
        params: {
          page: page + 1,
          page_size: pageSize
        }
      });
      const data = response.data;
      if (data?.results) {
        setNoShows(data.results);
        setTotal(data.count);
      } else {
        setNoShows(data);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching no-shows:', err);
      setError('Erreur lors du chargement des absences');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNoShow = async () => {
    setDeleting(true);
    try {
      await axiosClient.delete(`/superadmin/no-shows/${deleteDialog.noShowId}/`);
      setDeleteDialog({ open: false, noShowId: null });
      fetchNoShows();
    } catch (err) {
      console.error('Error deleting no-show:', err);
      setError('Erreur lors de la suppression de l\'absence');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pb: 12 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: theme.colors.text.primary }}>
        <EventBusyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Historique des Absences (No-Shows)
      </Typography>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.colors.primary[50] }}>
                <TableCell sx={{ fontWeight: 700 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Salon</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Service</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date du RDV</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Marqué par</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date de marquage</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sanction</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {noShows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Aucune absence enregistrée</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                noShows.map((noShow) => (
                  <TableRow key={noShow.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {noShow.client_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {noShow.client_email || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {noShow.salon_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {noShow.service_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {moment(noShow.appointment_date).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {noShow.marked_by_name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {moment(noShow.marked_at).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {noShow.sanction_applied ? (
                        <Chip
                          label={noShow.sanction_applied}
                          size="small"
                          color="error"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Aucune
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Retirer cette absence">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, noShowId: noShow.id })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {total > 0 && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: { xs: 0, md: '280px' },
          zIndex: 1000,
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <Paper
            elevation={3}
            sx={{
              pointerEvents: 'auto',
              borderRadius: theme.borderRadius.xl,
              border: `1px solid ${theme.colors.border.light}`,
              overflow: 'hidden',
              width: 'fit-content'
            }}
          >
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Lignes par page"
              sx={{ bgcolor: 'white' }}
            />
          </Paper>
        </Box>
      )}

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, noShowId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir retirer cette absence ? Cette action est irréversible.
          </Typography>
          <Typography color="warning.main" sx={{ mt: 2, fontSize: '0.9rem' }}>
            ⚠️ Attention : Si cette absence a déclenché un ban, vous devrez également gérer le ban séparément.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, noShowId: null })} disabled={deleting}>
            Annuler
          </Button>
          <Button
            onClick={handleDeleteNoShow}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NoShowsList;
